import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Component,
  inject,
  Inject,
  OnInit,
  PLATFORM_ID,
  ViewChild,
  HostListener,
  ElementRef,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ChartData, ChartEvent, ChartType, ChartConfiguration } from 'chart.js';
import {
  BaseChartDirective,
  provideCharts,
  withDefaultRegisterables,
} from 'ng2-charts';
import { Api } from '../../services/api';
import { Router } from '@angular/router';
import { PermissionService } from '../../services/permission.service';

interface Meta {
  id: string;
  mes: string;
  progreso_actual: number;
  valor_objetivo: number;
  descripcion?: string;
  fecha_inicio?: string;
  usuario_id?: string;
  nombre_completo?: string;
}
interface EjecutivoData {
  nombre: string;
  avance_real: number;
  meta_proyectada: number;
}

@Component({
  selector: 'app-goals-graphic',
  imports: [BaseChartDirective, CommonModule, FormsModule],
  templateUrl: './goals-graphic.html',
  styleUrl: './goals-graphic.css',
  providers: [provideCharts(withDefaultRegisterables())],
})
export class GoalsGraphicComponent implements OnInit, AfterViewInit {
  @ViewChild(BaseChartDirective) chart: BaseChartDirective | undefined;

  public showMenu = false;
  public selectedChart: 'line' = 'line';

  private api = inject(Api);
  isBrowser: boolean = false;

  public metas: Meta[] = [];

  public metasEnCursoFormatted: string = '$0';
  public porcentajeCumplimiento: number = 0;
  public totalAlcanzadoFormatted: string = '$0';
  public oportunidadesGanadas: number = 0;
  public barChartExecutivesLabels: string[] = [];
  public barChartExecutivesDatasets: any[] = [];
  public barChartExecutivesOptions: any;
  public executivesDisplayData: EjecutivoData[] = [];
  public executiveColors: Map<string, string> = new Map();
  public weeklyData: any[] = [];
  public lineChartWeeklyData: any = {};
  public lineChartWeeklyOptions: any = {};

  public lineChartLabels: string[] = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ];

  public lineChartDatasets: ChartData<'line'>['datasets'] = [
    {
      data: [],
      label: 'Avance real',
      borderColor: '#EA5A10',
      backgroundColor: '#EA5A10',
      pointBackgroundColor: '#EA5A10',
      pointBorderColor: '#ffffff',
      pointBorderWidth: 2,
      pointRadius: 6,
      pointHoverRadius: 8,
      fill: false,
      tension: 0.4,
      spanGaps: false,
    },
    {
      data: [],
      label: 'Metas proyectada',
      borderColor: '#8D8D8D',
      backgroundColor: '#8D8D8D',
      pointBackgroundColor: '#8D8D8D',
      pointBorderColor: '#ffffff',
      pointBorderWidth: 2,
      pointRadius: 6,
      pointHoverRadius: 8,
      fill: false,
      tension: 0.4,
      spanGaps: false,
    },
  ];

  public doughnutChartLabels: string[] = ['Completado', 'Sin completar'];
  public doughnutChartDatasets: ChartData<'doughnut'>['datasets'] = [
    {
      data: [0, 0],
      label: 'Cumplimiento',
      backgroundColor: ['#F47C2C', '#E7E7E7'],
    },
  ];
  public doughnutChartOptions: ChartConfiguration<'doughnut'>['options'] = {
    responsive: true,
    cutout: '70%',
    plugins: {
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.label || '';
            const value = context.parsed;
            const total = context.dataset.data.reduce(
              (a: number, b: number) => a + b,
              0
            );
            const percentage =
              total > 0 ? ((value / total) * 100).toFixed(1) + '%' : '0%';
            return `${label}: ${this.formatCurrencyPublic(
              value
            )} (${percentage})`;
          },
        },
      },
    },
  };

  loadMonthlyComplianceData() {
    const currentMonth = new Date().getMonth() + 1;
    const currentYear = new Date().getFullYear();

    console.log(
      `Cargando datos de cumplimiento para: ${currentMonth}/${currentYear}`
    );

    this.api.getMonthlyComplianceData(currentMonth, currentYear).subscribe({
      next: (data: any) => {
        console.log('Datos recibidos:', data);

        if (data.success) {
          const totalMeta = data.total_meta || 0;
          const totalCompletado = data.total_completado || 0;
          const totalSinCompletar = data.total_sin_completar || 0;

          this.doughnutChartDatasets[0].data = [
            totalCompletado,
            totalSinCompletar,
          ];

          const porcentajeCompletado =
            totalMeta > 0 ? (totalCompletado / totalMeta) * 100 : 0;
          const porcentajeSinCompletar =
            totalMeta > 0 ? (totalSinCompletar / totalMeta) * 100 : 100;

          this.doughnutChartLabels = [
            `Completado ${porcentajeCompletado.toFixed(0)}%`,
            `Sin completar ${porcentajeSinCompletar.toFixed(0)}%`,
          ];

          console.log(
            `Gráfico actualizado: Completado=${totalCompletado}, Sin completar=${totalSinCompletar}`
          );

          setTimeout(() => {
            this.chart?.update();
          }, 100);
        } else {
          console.error('Error en la respuesta del servidor:', data.message);
        }
      },
      error: (err: any) => {
        console.error('Error al cargar datos de cumplimiento:', err);
        this.doughnutChartDatasets[0].data = [0, 100];
        this.doughnutChartLabels = ['Completado 0%', 'Sin completar 100%'];
        this.chart?.update();
      },
    });
  }

  getBackgroundColor(index: number): string {
    const colors = ['#F47C2C', '#E7E7E7'];
    return colors[index] || '#000000';
  }

  getFormattedData(index: number): string {
    const dataset = this.doughnutChartDatasets[0];
    const value = dataset?.data?.[index] || 0;
    return this.formatCurrencyPublic(value);
  }

  public lineChartOptions: ChartConfiguration<'line'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        align: 'start',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 20,
          font: {
            size: 12,
          },
          boxWidth: 12,
          boxHeight: 12,
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0,0,0,0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#ddd',
        borderWidth: 1,
        callbacks: {
          label: function (context: any) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += '$' + (context.parsed.y / 1000000).toFixed(1) + 'M';
            }
            return label;
          },
        },
      },
    },
    scales: {
      x: {
        type: 'category',
        display: true,
        position: 'bottom',
        grid: {
          display: true,
          color: 'rgba(0,0,0,0.1)',
        },
        ticks: {
          font: {
            size: 11,
          },
          maxRotation: 0,
          minRotation: 0,
        },
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        beginAtZero: true,
        min: 0,
        suggestedMax: 60000000,
        grid: {
          display: true,
          color: 'rgba(0,0,0,0.1)',
        },
        ticks: {
          font: {
            size: 11,
          },
          stepSize: 20000000,
          callback: function (value: any) {
            return '$' + (value / 1000000).toFixed(0) + 'M';
          },
        },
      },
    },
    elements: {
      point: {
        radius: 4,
        hoverRadius: 6,
      },
      line: {
        tension: 0.4,
        borderWidth: 2,
      },
    },
    layout: {
      padding: {
        top: 10,
        bottom: 10,
        left: 15,
        right: 15,
      },
    },
  };

  constructor(
    @Inject(PLATFORM_ID) private platformId: any,
    private elementRef: ElementRef,
        private router: Router,
    public permissionService: PermissionService,


    
  ) {}

  ngOnInit(): void {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.getGoalsData();
    this.initializeExecutivesBarChart();
    this.loadMonthlyComplianceData();
    this.loadWeeklyData()
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.chart?.update('active');
    }, 300);
  }

  getGoalsData(): void {
    console.log('=== CARGANDO METAS DESDE BACKEND (usando getMetas) ===');

    this.api.getMetas().subscribe({
      next: (response: any) => {
        console.log('Respuesta del backend via getMetas():', response);

        if (Array.isArray(response) && response.length > 0) {
          console.log(`${response.length} metas recibidas del backend`);

          this.metas = response.map((item: any) => ({
            id: item.id || item.meta_id || 'sin-id',
            mes: item.mes || 'Enero',
            progreso_actual: this.convertToNumber(item.progreso_actual),
            valor_objetivo: this.convertToNumber(item.valor_objetivo),
            descripcion: item.descripcion || '',
            fecha_inicio: item.fecha_inicio || '',
            usuario_id: item.usuario_id || 'sin-asignar',
            nombre_completo: item.nombre_completo,
          }));

          console.log('Metas procesadas:', this.metas);
          console.log('Nombres de usuarios encontrados:', [
            ...new Set(this.metas.map((m) => m.nombre_completo)),
          ]);

          this.processChartData();
          this.updateSummaryCards();
          this.updateChart();
          this.processExecutivesData();
        } else {
          console.log('No se recibieron metas o respuesta vacía');
          this.clearData();
        }
      },
      error: (err: any) => {
        console.error('Error al obtener metas con getMetas():', err);
        this.clearData();
      },
    });
  }

  private convertToNumber(value: any): number {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') return parseFloat(value) || 0;
    return 0;
  }

  private processChartData(): void {
    console.log('=== PROCESANDO DATOS CON SUMA DE TODAS LAS METAS POR MES ===');
    console.log('Metas recibidas:', this.metas);

    const metasProyectadaData: number[] = new Array(12).fill(0);
    const avanceRealData: number[] = new Array(12).fill(0);

    this.metas.forEach((meta) => {
      const mesIndex = this.lineChartLabels.findIndex(
        (mes) => mes === meta.mes
      );

      if (mesIndex !== -1) {
        const valorObjetivo = meta.valor_objetivo;
        const avanceReal = meta.progreso_actual * valorObjetivo;

        metasProyectadaData[mesIndex] += valorObjetivo;
        avanceRealData[mesIndex] += Math.round(avanceReal);

        console.log(
          `📊 ${meta.mes}: +$${(valorObjetivo / 1000000).toFixed(
            1
          )}M objetivo, +${(meta.progreso_actual * 100).toFixed(1)}% avance`
        );
      } else {
        console.log(`❌ ${meta.mes}: Mes no encontrado en labels`);
      }
    });

    console.log('=== RESUMEN POR MES ===');
    this.lineChartLabels.forEach((mesLabel, index) => {
      const totalMeta = metasProyectadaData[index];
      const totalAvance = avanceRealData[index];
      const porcentaje = totalMeta > 0 ? (totalAvance / totalMeta) * 100 : 0;

      console.log(
        `${mesLabel}: $${(totalMeta / 1000000).toFixed(1)}M objetivo, $${(
          totalAvance / 1000000
        ).toFixed(1)}M avance (${porcentaje.toFixed(1)}%)`
      );
    });

    this.lineChartDatasets[1].data = metasProyectadaData;
    this.lineChartDatasets[0].data = avanceRealData;

    console.log('=== GRÁFICO ACTUALIZADO CON SUMA ACUMULATIVA ===');
  }
  public executivesListData: any[] = [];

  private processExecutivesData(): void {
    console.log('=== PROCESANDO DATOS PARA EJECUTIVOS ===');

    const ejecutivosMap = new Map<string, EjecutivoData>();
    this.executiveColors.clear();

    this.metas.forEach((meta) => {
      const usuarioId = meta.usuario_id || 'sin-asignar';
      const nombreCompleto = meta.nombre_completo || `Ejecutivo ${usuarioId}`;

      if (!ejecutivosMap.has(usuarioId)) {
        ejecutivosMap.set(usuarioId, {
          nombre: nombreCompleto,
          avance_real: 0,
          meta_proyectada: 0,
        });

        this.executiveColors.set(
          usuarioId,
          this.getExecutiveColor(ejecutivosMap.size - 1)
        );
      }

      const ejecutivo = ejecutivosMap.get(usuarioId)!;
      ejecutivo.meta_proyectada += meta.valor_objetivo;
      ejecutivo.avance_real += meta.valor_objetivo * meta.progreso_actual;
    });

    const ejecutivosArray = Array.from(ejecutivosMap.values());
    ejecutivosArray.sort((a, b) => b.avance_real - a.avance_real);

    this.executivesListData = ejecutivosArray.map((ejecutivo) => {
      const usuarioId = this.getUserIdByName(ejecutivo.nombre);
      return {
        ...ejecutivo,
        color: this.executiveColors.get(usuarioId) || '#EA5A10',
        formattedAvance: this.formatCurrency(ejecutivo.avance_real),
      };
    });

    const top10Ejecutivos = ejecutivosArray.slice(0, 10);
    this.updateExecutivesChart(top10Ejecutivos);
  }

  private getUserIdByName(nombre: string): string {
    const meta = this.metas.find((m) => m.nombre_completo === nombre);
    return meta?.usuario_id || nombre;
  }

  private updateExecutivesChart(ejecutivos: EjecutivoData[]): void {
    this.barChartExecutivesLabels = ejecutivos.map(
      (ejecutivo) => ejecutivo.nombre
    );

    const avanceRealColors = ejecutivos.map((ejecutivo) => {
      const usuarioId = this.getUserIdByName(ejecutivo.nombre);
      return this.executiveColors.get(usuarioId) || '#EA5A10';
    });

    const metaProyectadaColors = ejecutivos.map(() => '#E7E7E7');

    this.barChartExecutivesDatasets = [
      {
        label: 'Avance Real',
        data: ejecutivos.map((ejecutivo) => ejecutivo.avance_real),
        backgroundColor: avanceRealColors,
        borderColor: avanceRealColors,
        borderWidth: 0,
        barPercentage: 0.7,
      },
      {
        label: 'Faltante para Meta',
        data: ejecutivos.map((ejecutivo) => {
          return Math.max(0, ejecutivo.meta_proyectada - ejecutivo.avance_real);
        }),
        backgroundColor: metaProyectadaColors,
        borderColor: '#CCCCCC',
        borderWidth: 1,
        barPercentage: 0.7,
      },
    ];
  }
  private getExecutiveColor(index: number): string {
    const colors = [
      '#8B4513',
      '#D2691E',
      '#CD853F',
      '#F4A460',
      '#DEB887',
      '#D2B48C',
      '#BC8F8F',
      '#A0522D',
      '#8B7355',
      '#B8860B',
      '#DAA520',
      '#FF8C00',
      '#FFA500',
      '#FF7F50',
      '#CD5C5C',
    ];

    return colors[index % colors.length];
  }

  private lightenColor(color: string, percent: number): string {
    const num = parseInt(color.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = ((num >> 8) & 0x00ff) + amt;
    const B = (num & 0x0000ff) + amt;
    return (
      '#' +
      (
        0x1000000 +
        (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
        (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
        (B < 255 ? (B < 1 ? 0 : B) : 255)
      )
        .toString(16)
        .slice(1)
    );
  }

  private darkenColor(color: string, percent: number): string {
    const num = parseInt(color.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) - amt;
    const G = ((num >> 8) & 0x00ff) - amt;
    const B = (num & 0x0000ff) - amt;
    return (
      '#' +
      (
        0x1000000 +
        (R > 0 ? (R > 255 ? 255 : R) : 0) * 0x10000 +
        (G > 0 ? (G > 255 ? 255 : G) : 0) * 0x100 +
        (B > 0 ? (B > 255 ? 255 : B) : 0)
      )
        .toString(16)
        .slice(1)
    );
  }

  private initializeExecutivesBarChart(): void {
    this.barChartExecutivesOptions = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          stacked: true,
          display: false,
          grid: {
            display: false,
          },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          ticks: {
            callback: function (value: any) {
              return '' + (value / 1000000).toFixed(1) + 'M';
            },
            font: {
              size: 11,
            },
          },
          grid: {
            color: 'rgba(0,0,0,0.1)',
          },
          position: 'left',
          afterFit: function (scaleInstance: any) {
            scaleInstance.width = 50;
          },
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          mode: 'index',
          callbacks: {
            label: function (context: any) {
              let label = context.dataset.label || '';
              if (label) {
                label += ': ';
              }
              if (context.parsed.y !== null) {
                const value = context.parsed.y;
                label += '$' + (value / 1000000).toFixed(2) + 'M';
              }
              return label;
            },
            afterBody: function (context: any) {
              if (context.length > 1) {
                const total = context.reduce(
                  (sum: number, tooltipItem: any) => {
                    return sum + tooltipItem.parsed.y;
                  },
                  0
                );
                return ['Total: $' + (total / 1000000).toFixed(2) + 'M'];
              }
              return undefined;
            },
          },
        },
      },
      layout: {
        padding: {
          left: 10,
          right: 10,
          top: 10,
          bottom: 10,
        },
      },
    };
  }

  private updateSummaryCards(): void {
    if (this.metas.length === 0) {
      this.clearData();
      return;
    }

    const totalMetasEnCurso = this.metas.reduce(
      (sum, meta) => sum + meta.valor_objetivo,
      0
    );
    this.metasEnCursoFormatted = this.formatCurrency(totalMetasEnCurso);

    const promedioProgreso =
      this.metas.reduce((sum, meta) => sum + meta.progreso_actual, 0) /
      this.metas.length;
    this.porcentajeCumplimiento = Math.round(promedioProgreso * 100);

    const totalAlcanzado = this.metas.reduce(
      (sum, meta) => sum + meta.progreso_actual * meta.valor_objetivo,
      0
    );
    this.totalAlcanzadoFormatted = this.formatCurrency(totalAlcanzado);

    this.oportunidadesGanadas = this.metas.filter(
      (meta) => meta.progreso_actual >= 1.0
    ).length;

    console.log('=== RESUMEN ===');
    console.log('Total en curso:', this.metasEnCursoFormatted);
    console.log('% Cumplimiento:', this.porcentajeCumplimiento + '%');
    console.log('Total alcanzado:', this.totalAlcanzadoFormatted);
    console.log('Oportunidades ganadas:', this.oportunidadesGanadas);
  }

  private clearData(): void {
    this.lineChartDatasets[0].data = new Array(12).fill(0);
    this.lineChartDatasets[1].data = new Array(12).fill(0);
    this.metasEnCursoFormatted = '$0';
    this.porcentajeCumplimiento = 0;
    this.totalAlcanzadoFormatted = '$0';
    this.oportunidadesGanadas = 0;
    this.updateChart();
  }

  public formatCurrencyPublic(value: number): string {
    return this.formatCurrency(value);
  }

  private formatCurrency(value: number): string {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)} M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)} K`;
    } else {
      return `$${value.toLocaleString()}`;
    }
  }

  public updateChart(): void {
    if (this.chart && this.isBrowser) {
      setTimeout(() => {
        this.chart?.update('none');
      }, 50);

      setTimeout(() => {
        this.chart?.update('active');
      }, 100);
    }
  }

  cargarDatosReales(): void {
    console.log('=== RECARGANDO DATOS CON getMetas() ===');
    this.metas = [];
    this.clearData();
    this.getGoalsData();
  }
  getAvanceRealData(index: number): number {
    return (this.lineChartDatasets[0]?.data?.[index] as number) || 0;
  }

  getMetasProyectadaData(index: number): number {
    return (this.lineChartDatasets[1]?.data?.[index] as number) || 0;
  }

  public chartClicked(event: any): void {
    console.log('Chart clicked:', event);
  }

  public chartHovered(event: any): void {
    console.log('Chart hovered:', event);
  }

  showGraphSelection() {
    this.showMenu = !this.showMenu;
  }

  @HostListener('document:click', ['$event.target'])
  onClickOutside(target: EventTarget | null): void {
    if (!(target instanceof HTMLElement)) return;

    const clickedInsideRecent = this.elementRef.nativeElement
      .querySelector('.dropdown-trigger')
      ?.contains(target);

    if (!clickedInsideRecent) {
      this.showMenu = false;
    }
  }

loadWeeklyData() {
  this.api.getWeeklyProgress().subscribe({
    next: (res: any) => {
      const data = res.data || [];
      const semanas = data.map((d: any) => `${d.semana}° Semana`);

      this.weeklyData = data.map((d: any) => ({
        semana: d.semana,
        avanceReal: d.avance_real,
        metaProyectada: d.meta_proyectada,
      }));

      this.lineChartWeeklyData = {
        labels: semanas,
        datasets: [
          {
            label: 'Avance Real',
            data: this.weeklyData.map((s) => s.avanceReal),
            borderColor: '#EA5A10',
            backgroundColor: 'rgba(244, 124, 44, 0.1)',
            pointBackgroundColor: '#EA5A10',
            pointBorderColor: '#FFFFFF',
            pointBorderWidth: 2,
            pointRadius: 6,
            tension: 0.3,
            fill: false,
          },
          {
            label: 'Meta Proyectada',
            data: this.weeklyData.map((s) => s.metaProyectada),
            borderColor: '#8D8D8D',
            backgroundColor: 'rgba(231, 231, 231, 0.1)',
            pointBackgroundColor: '#8D8D8D',
            pointBorderColor: '#8D8D8D',
            pointBorderWidth: 2,
            pointRadius: 6,
            tension: 0.3,
            fill: false,
          },
        ],
      };

      const maxValue = Math.max(
        ...this.weeklyData.map((d) => Math.max(d.avanceReal, d.metaProyectada))
      );
      const minValue = Math.min(
        ...this.weeklyData.map((d) => Math.min(d.avanceReal, d.metaProyectada))
      );

      this.lineChartWeeklyOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              label: (context: any) => {
                const label = context.dataset.label || '';
                const value = context.parsed.y;
                return `${label}: ${this.formatCurrencyPublic(value)}`;
              },
            },
          },
        },
        scales: {
          x: {
            grid: {
              display: false,
            },
            ticks: {
              color: '#666',
              font: {
                size: 11,
              },
            },
          },
          y: {
            beginAtZero: false,
            min: Math.max(0, minValue * 0.8), 
            max: maxValue * 1.2, 
            grid: {
              color: '#E7E7E7',
            },
            ticks: {
              color: '#666',
              callback: (value: any) => {
                if (value >= 1000000) {
                  return `$${(value / 1000000).toFixed(1)}M`;
                } else if (value >= 1000) {
                  return `$${(value / 1000).toFixed(0)}K`;
                }
                return `$${value}`;
              },
            },
          },
        },
      };

      this.chart?.update();
    },
    error: (err) => {
      console.error('Error al cargar progreso semanal:', err);
    },
  });
}

  /* initializeWeeklyData() {
    const semanasEnMes = 5;
    const metaMensual = 2500000;

    const avancesReales = [780000, 320000, 750000, 350000, 100000];

    const metaProyectadaSemanal = [500000, 1300000, 2000000, 2400000, 3000000];

    let avanceAcumuladoReal = 0;
    this.weeklyData = [];

    for (let i = 0; i < semanasEnMes; i++) {
      avanceAcumuladoReal += avancesReales[i];

      this.weeklyData.push({
        semana: i + 1,
        avanceReal: avanceAcumuladoReal,
        metaProyectada: metaProyectadaSemanal[i],
      });
    }

    this.lineChartWeeklyData = {
      labels: ['1° Semana', '2° Semana', '3° Semana', '4° Semana', '5° Semana'],
      datasets: [
        {
          label: 'Avance Real',
          data: this.weeklyData.map((s) => s.avanceReal),
          borderColor: '#EA5A10',
          backgroundColor: 'rgba(244, 124, 44, 0.1)',
          pointBackgroundColor: '#EA5A10',
          pointBorderColor: '#FFFFFF',
          pointBorderWidth: 2,
          pointRadius: 6,
          tension: 0.3,
          fill: false,
        },
        {
          label: 'Meta Proyectada',
          data: this.weeklyData.map((s) => s.metaProyectada),
          borderColor: '#8D8D8D',
          backgroundColor: 'rgba(231, 231, 231, 0.1)',
          pointBackgroundColor: '#8D8D8D',
          pointBorderColor: '#8D8D8D',
          pointBorderWidth: 2,
          pointRadius: 6,
          tension: 0.3,
          fill: false,
        },
      ],
    };

    this.lineChartWeeklyOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const label = context.dataset.label || '';
              const value = context.parsed.y;
              return `${label}: ${this.formatCurrencyPublic(value)}`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            color: '#666',
            font: {
              size: 11,
            },
          },
        },
        y: {
          beginAtZero: false,
          min: 400000,
          max: 3200000,
          grid: {
            color: '#E7E7E7',
          },
          ticks: {
            color: '#666',
            callback: (value: any) => {
              if (value >= 1000000) {
                return `$${(value / 1000000).toFixed(1)}M`;
              } else if (value >= 1000) {
                return `$${(value / 1000).toFixed(0)}K`;
              }
              return `$${value}`;
            },
          },
        },
      },
    };
  }

  getSemanasEnMes(year: number, month: number): number {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const firstDayOfWeek = firstDay.getDay();

    return Math.ceil((daysInMonth + firstDayOfWeek) / 7);
  }

  getSemanaNombre(index: number): string {
    const semanas = [
      '1ª Semana',
      '2ª Semana',
      '3ª Semana',
      '4ª Semana',
      '5ª Semana',
    ];
    return semanas[index] || `${index + 1}ª Semana`;
  }

  getTotalAvanceReal(): string {
    const total =
      this.weeklyData.length > 0
        ? this.weeklyData[this.weeklyData.length - 1].avanceReal
        : 0;
    return this.formatCurrencyPublic(total);
  }

  getMetaProyectada(): string {
    return this.formatCurrencyPublic(2500000);
  }

 
 */

   getMesActual(): string {
    const meses = [
      'Enero',
      'Febrero',
      'Marzo',
      'Abril',
      'Mayo',
      'Junio',
      'Julio',
      'Agosto',
      'Septiembre',
      'Octubre',
      'Noviembre',
      'Diciembre',
    ];
    return meses[new Date().getMonth()];
  }
  
    goToGoals() {
    this.router.navigate(['/goals']);
  }

}
